
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { checkIfLoggedIn, redeemAutoLoginToken } from '../../store/actions/authenticationActions'
import { CircularProgress } from '@material-ui/core';

import { getUserInfo } from '../../store/actions/accountActions'
import { getEntryParams, getNextPath, stripTokenFromSearch } from '../shared/entryParams'

export default function withAuthentication(ComposedComponent) {

    class Authentication extends Component {
        state = {
            loading: true
        }

        componentDidMount() {
            if (this.props.isLoginTokenValid) {
                // Already signed in: an entry link (?next=...) goes straight on,
                // and a token it carried is not needed.
                if (this.continueToNext()) { return }
                this.clearTokenFromUrl()
                this.setState({ loading: false })
                return
            }

            // One-click links from email templates arrive as /#/?token=<oneTimeToken>.
            // Try that first: it is the only thing that can sign somebody in who
            // has no session here yet. The token is single-use and short-lived,
            // and the API swaps it for an ordinary session.
            const { token, hasToken } = getEntryParams(this.props.location && this.props.location.search)

            if (hasToken) {
                this.signInWithToken(token)
                return
            }

            this.resumeStoredSession()
        }

        signInWithToken(token) {
            try {
                this.props.redeemAutoLoginToken(token, (err) => {
                    // Signed in by the link and it names a destination: go there.
                    // That replace drops the whole query, token included.
                    if (!err && this.continueToNext()) { return }

                    // Otherwise clear the token from the URL either way. It is
                    // wanted once, on arrival, and leaving it there means it
                    // persists in history, gets copied when the link is shared,
                    // and rides along in the Referer header of later requests.
                    // A dead token also marks the link expired, so the login
                    // form can say why.
                    this.clearTokenFromUrl({ expired: !!err })

                    if (err) {
                        // A dead or mangled link should look like an ordinary
                        // visit, not an error page. Fall back to whatever
                        // session is stored, which usually means the login form.
                        // The URL keeps email / next / renewalId (only the token
                        // is stripped), so that form is prefilled and locked when
                        // the link carried the email, and signing in continues
                        // to `next` (myAccount's loginFn).
                        this.resumeStoredSession()
                        return
                    }

                    this.setState({ loading: false })
                })
            } catch (err) {
                this.clearTokenFromUrl({ expired: true })
                this.resumeStoredSession()
            }
        }

        resumeStoredSession() {
            try {
                this.props.checkIfLoggedIn((currentUser) => {
                    if (currentUser && this.continueToNext()) { return }
                    this.setState({ loading: false })
                })
            } catch (err) {
                this.setState({ loading: false })
            }
        }

        // Signed in and the URL names an allowed destination (entryParams
        // NEXT_DESTINATIONS): go there instead of rendering this route. replace,
        // not push, and with an empty search, so the token and the entry params
        // are gone from history. Returns true when it navigated.
        //
        // Whether the renewal belongs to the member is checked by the
        // destination page (#25) against the API, not here.
        continueToNext() {
            const { history, location } = this.props
            if (!history || !location) { return false }

            const nextPath = getNextPath(location.search)
            if (!nextPath) { return false }

            history.replace({ pathname: nextPath, search: '' })
            return true
        }

        clearTokenFromUrl(opts) {
            const { history, location } = this.props
            if (!history || !location) { return }

            const search = stripTokenFromSearch(location.search, opts)
            if (search === location.search) { return }

            // replace, not push: the token-bearing URL should not be reachable
            // with the back button.
            history.replace({ pathname: location.pathname, search, hash: location.hash })
        }

        render() {
            return !this.state.loading ? <ComposedComponent {...this.props} /> : <div role="status" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '96px 16px', color: '#646b78' }}><CircularProgress />Loading your account…</div>
        }
    }

    const mapStateToProps = (state) => {
        return {
            isLoginTokenValid: state.authentication.isLoginTokenValid,
            currentUser: state.authentication.currentUser
        };
    }
    const mapDispatchToProps = (dispatch) => {
        return {
            checkIfLoggedIn: (token) => dispatch(checkIfLoggedIn(token)),
            redeemAutoLoginToken: (token, cb) => dispatch(redeemAutoLoginToken(token, cb)),
            getUserInfo: (userId, cb) => dispatch(getUserInfo(userId, cb)),
        }
    }
    return connect(mapStateToProps, mapDispatchToProps)(Authentication);
}
